import _ from 'lodash'

/**
 * Groups a flat list of plans by their clientId.
 * @param {Array<Object>} plans - Plans to group (each must have a clientId field).
 * @returns {Object<string, Array<Object>>} Plans grouped by clientId.
 */
export const groupPlansByClientId = (plans) =>
  [...plans].reduce(
    (result, item) => ({
      ...result,
      [item.clientId]: [...(result[item.clientId] || []), item]
    }),
    {}
  )

/**
 * Finds the currently selected option in a dropdown options list.
 * @param {Array<Object>} options - Dropdown options, each optionally flagged with isSelected.
 * @returns {Object|undefined} The selected option, if any.
 */
export const findSelectedOption = (options) =>
  options?.find((opt) => opt.isSelected)

/**
 * Filters JV and HRP plans down to those that carry a networkAffiliation
 * pointing at one of the currently available network options.
 * @param {Array<Object>} jvPlans - Javelina plans.
 * @param {Array<Object>} hrpPlans - HRP plans.
 * @param {Array<{title: string, value: string}>} networkOptions - Available network name options.
 * @returns {Array<Object>} Plans (JV + HRP) that are affiliated to a known network.
 */
export const getPlansWithNetworkAffiliation = (jvPlans, hrpPlans, networkOptions) =>
  [...jvPlans, ...hrpPlans].filter((p) => {
    if (!p.networkAffiliation) return false
    return networkOptions
      .map((netOpt) => netOpt.value)
      .includes(p.networkAffiliation.uuid)
  })

/**
 * Attaches networkAffiliationOptions to a single plan based on available network options.
 * @param {Object} plan - Plan to annotate.
 * @param {Array<{title: string, value: string}>} networkOptions - Available network name options.
 * @param {boolean} mutateNetworkOptions - When true, mutates networkOptions entries (buildClientOptionsList side effect).
 * @returns {Object} Plan with networkAffiliationOptions attached.
 */
export const attachNetworkAffiliationOptionsToPlan = (plan, networkOptions, mutateNetworkOptions = false) => {
  const networkAffiliationOptions = networkOptions.map((netOpt) => {
    const isSelected = plan.networkAffiliation && plan.networkAffiliation.uuid === netOpt.value
    if (mutateNetworkOptions) {
      netOpt.isSelected = isSelected
      return netOpt
    }
    return { ...netOpt, isSelected }
  })

  return {
    ...plan,
    networkAffiliationOptions: JSON.parse(JSON.stringify(networkAffiliationOptions))
  }
}

/**
 * Attaches fresh networkAffiliationOptions to each plan while preserving networkAffiliation.
 * @param {Array<Object>} plans - Plans to annotate.
 * @param {Array<{title: string, value: string}>} networkOptions - Available network name options.
 * @param {boolean} mutateNetworkOptions - When true, mutates networkOptions entries in place.
 * @returns {Array<Object>} Plans sorted by planId with networkAffiliationOptions attached.
 */
export const buildPlansWithNetworkAffiliationOptions = (plans, networkOptions, mutateNetworkOptions = false) =>
  [...plans]
    .map((plan) => attachNetworkAffiliationOptionsToPlan(plan, networkOptions, mutateNetworkOptions))
    .sort((a, b) => a.planId.localeCompare(b.planId))

export const buildClientOptionsList = (plansGroupedByClientId, networkOptions, currentClientName, selectedClientId) => {
  const options = Object.keys(plansGroupedByClientId)
    .map((clientId) => ({
      title: clientId,
      value: clientId,
      plans: buildPlansWithNetworkAffiliationOptions(
        plansGroupedByClientId[clientId],
        networkOptions,
        true
      ).map((planWithOptions) => {
        const planInGroup = plansGroupedByClientId[clientId].find((p) => p.planId === planWithOptions.planId)
        if (planInGroup) {
          planInGroup.networkAffiliationOptions = planWithOptions.networkAffiliationOptions
        }
        return planInGroup ?? planWithOptions
      }),
      disabled: plansGroupedByClientId[clientId].some(
        (plan) =>
          plan.networkAffiliation &&
          plan.networkAffiliation.clientName !== currentClientName
      ),
      isSelected: selectedClientId === clientId
    }))
    .sort((a, b) => a.title.localeCompare(b.title))

  options.unshift({
    title: 'NONE',
    plans: null,
    disabled: false,
    isSelected: false
  })

  return options
}

/**
 * Resolves the clientId after the user switches the plan type radio button.
 * @param {string} planType - The newly selected plan type ("Javelina" or "HRP").
 * @param {Object|null} configuredClient - The DB-backed configured client snapshot.
 * @returns {{clientId: string}} The new clientId for the selected plan type.
 */
export const resolvePlanTypeSelection = (planType, configuredClient) => {
  if (configuredClient?.planType === planType) {
    return { clientId: configuredClient.title }
  }
  return { clientId: 'NONE' }
}

/**
 * Builds the selectedClient object for a newly chosen clientId from grouped plans.
 * @param {string} clientId - The clientId chosen in the dropdown.
 * @param {Object<string, Array<Object>>} plansGroupedByClientId - Plans grouped by clientId.
 * @param {Array<{title: string, value: string}>} networkOptions - Available network name options.
 * @returns {{title: string, value: string, plans: Array<Object>|undefined}} The new selectedClient.
 */
export const buildSelectedClientFromGroupedPlans = (clientId, plansGroupedByClientId, networkOptions) => {
  const clientPlans = plansGroupedByClientId[clientId]

  if (!clientPlans?.length) {
    return {
      title: clientId,
      value: clientId,
      plans: undefined
    }
  }

  return {
    title: clientId,
    value: clientId,
    plans: buildPlansWithNetworkAffiliationOptions(clientPlans, networkOptions)
  }
}

/**
 * Applies a network affiliation selection to the plan matching planId, updating both
 * its networkAffiliationOptions (isSelected flags) and its networkAffiliation field.
 * @param {Array<Object>} plans - The selectedClient's plans.
 * @param {string} planId - The planId whose network affiliation changed.
 * @param {string} networkUuid - The uuid of the newly selected network.
 * @param {string} currentClientName - The clientName of the client currently being edited (from route params).
 * @returns {Array<Object>} The plans with the matching plan's affiliation updated.
 */
export const applyNetworkAffiliationSelection = (plans, planId, networkUuid, currentClientName) =>
  plans.map((p) => {
    if (p.planId === planId) {
      p.networkAffiliationOptions = p.networkAffiliationOptions.map((nao) => {
        if (networkUuid === nao.value) {
          nao.isSelected = true
          p.networkAffiliation = {
            networkName: nao.title,
            uuid: nao.value,
            clientName: currentClientName
          }
        }
        return nao
      })
    }
    return p
  })

/**
 * Resolves the client ID from a plan-affiliation client dropdown select payload.
 * @param {{ value?: string, title?: string }} payload - The select event payload.
 * @returns {string|undefined} The resolved client ID.
 */
export const resolveClientIdFromSelectPayload = (payload) =>
  payload?.value || payload?.title

/**
 * Resolves plan ID and network UUID from a plan-affiliation network dropdown select payload.
 * @param {{ name?: string, value?: string }} payload - The select event payload.
 * @returns {{ planId: string|undefined, networkUuid: string|undefined}} The resolved affiliation ids.
 */
export const resolveNetworkAffiliationFromSelectPayload = (payload) => ({
  planId: payload?.name,
  networkUuid: payload?.value
})

/**
 * Computes the key/value pairs present in a but not (identically) present in b.
 * @param {Object} a - The object to diff from.
 * @param {Object} b - The object to diff against.
 * @returns {Object} The pairs of a that differ from b.
 */
export const diffObjects = (a, b) =>
  _.fromPairs(_.differenceWith(_.toPairs(a), _.toPairs(b), _.isEqual))

/**
 * Compares the configured client snapshot against the currently selected client,
 * ignoring the derived networkAffiliationOptions field on each plan.
 * @param {Object} configuredClient - The DB-backed configured client snapshot.
 * @param {Object} selectedClient - The client currently selected in the tab.
 * @returns {boolean} True when the two are equivalent once networkAffiliationOptions is ignored.
 */
export const arePlanAffiliationsEqual = (configuredClient, selectedClient) =>
  _.isEqual(
    { ...configuredClient, plans: _.map(configuredClient?.plans, obj => _.omit(obj, ['networkAffiliationOptions'])) },
    { ...selectedClient, plans: _.map(selectedClient?.plans, obj => _.omit(obj, ['networkAffiliationOptions'])) }
  )

/**
 * Derives the clientId and planType implied by the first plan in a list of
 * network-affiliated plans.
 * @param {Array<Object>} plans - Plans with a network affiliation.
 * @returns {{clientId: string, planType: string}|null} The derived selection, or null when plans is empty.
 */
export const resolveClientAndPlanTypeFromPlans = (plans) => {
  const firstPlanInAffiliations = plans?.[0]
  if (!firstPlanInAffiliations) return null

  return {
    clientId: firstPlanInAffiliations?.clientId,
    planType: firstPlanInAffiliations?.planId.startsWith('HRP_') ? 'HRP' : 'Javelina'
  }
}

/**
 * Builds a deep-cloned snapshot of the configured client from saved/backend affiliations.
 * @param {string} clientId - The clientId derived from affiliated plans.
 * @param {string} planType - The plan type ("Javelina" or "HRP").
 * @param {Array<Object>} plans - The network-affiliated plans belonging to that client.
 * @returns {Object} The configuredClient snapshot.
 */
export const buildConfiguredClientSnapshot = (clientId, planType, plans) =>
  JSON.parse(
    JSON.stringify({
      title: clientId,
      value: clientId,
      planType,
      plans: [...plans]
        .filter((plan) => plan.clientId === clientId)
        .sort((a, b) => a.planId.localeCompare(b.planId)),
      disabled: false,
      isSelected: true
    })
  )

/**
 * Builds the payload emitted on the enableSavePlanAffiliation event whenever the
 * selectedClient changes.
 * @param {Object} selectedClient - The client currently selected in the tab.
 * @param {string} planType - The currently selected plan type.
 * @returns {Object} The payload to emit: the selected client plus planType, or a NONE sentinel.
 */
export const buildEnableSavePlanAffiliationPayload = (selectedClient, planType) => {
  if (selectedClient && selectedClient?.plans?.some((p) => p.networkAffiliation)) {
    return { ...selectedClient, planType }
  }
  return { title: 'NONE' }
}

/**
 * Filters dropdown options by the current search text, when text filtering is active.
 * @param {Array<Object>} options - Dropdown options, each with a title.
 * @param {boolean} filterByText - Whether text filtering is currently active.
 * @param {string} searchInputText - The text typed into the dropdown's search input.
 * @returns {Array<Object>} The filtered (or unfiltered) options.
 */
export const filterDropdownOptions = (options, filterByText, searchInputText) => {
  if (filterByText) {
    return options.filter((opt) =>
      opt.title.toLowerCase().includes(searchInputText.toLowerCase())
    )
  }
  return options
}

/**
 * Resolves the text to display in a dropdown's search input.
 * @param {boolean} resetSearchText - Whether the search text should be cleared.
 * @param {string} planType - The planType prop, when the dropdown is used as a plan-type-scoped selector.
 * @param {string} name - The dropdown's name prop.
 * @param {Array<Object>} options - Dropdown options, each optionally flagged with isSelected.
 * @returns {string|undefined} The text to display in the search input.
 */
export const resolveSearchInputText = (resetSearchText, planType, name, options) => {
  if (resetSearchText) return ''
  if (planType) return name
  return findSelectedOption(options)?.title
}

/**
 * Builds the network name dropdown options from a client's network configs.
 * @param {Array<Object>} networkConfigs - The client's draft network configs.
 * @returns {Array<{title: string, value: string}>} The network name options.
 */
export const buildNetworkNameOptions = (networkConfigs) =>
  networkConfigs
    ?.filter((nc) => nc && nc.name)
    .map((nc) => ({ title: nc.name, value: nc.uuid })) ?? []

/**
 * Builds the "client" dropdown option list (used in the parent client-affiliations summary)
 * from a flat list of plans and the currently available network names.
 * @param {Array<Object>} plans - All plans (JV + HRP) for the client's plan types.
 * @param {Array<{title: string, value: string}>} networkNames - Available network name options.
 * @param {string} currentClientName - The clientName of the client currently being edited (from route params).
 * @returns {Array<Object>} Client dropdown options, including a leading "NONE" entry.
 */
export const buildClientsFromPlans = (plans = [], networkNames, currentClientName) => {
  const groupByClientId = groupPlansByClientId(plans)

  const clients = Object.keys(groupByClientId).map((k) => {
    const disabled = groupByClientId[k].some(
      (plan) =>
        plan.networkAffiliation &&
        plan.networkAffiliation.clientName !== currentClientName
    )
    const isSelected = groupByClientId[k].some(
      (plan) =>
        plan.networkAffiliation &&
        networkNames &&
        networkNames.map((nn) => nn.value).includes(plan.networkAffiliation.uuid)
    )

    const oneOfTheSelectedPlan = groupByClientId[k].find(
      (plan) =>
        plan.networkAffiliation &&
        networkNames &&
        networkNames.map((nn) => nn.value).includes(plan.networkAffiliation.uuid)
    )

    const planType = oneOfTheSelectedPlan?.planId.includes('HRP_')
      ? 'HRP'
      : 'Javelina'

    return {
      title: k,
      plans: groupByClientId[k],
      planType,
      disabled,
      isSelected
    }
  })

  clients.sort((a, b) => a.title.localeCompare(b.title))

  clients.unshift({
    title: 'NONE',
    plans: null,
    disabled: false,
    isSelected: false
  })

  return clients
}

/**
 * Extracts the network affiliation uuids present in a list of plans.
 * @param {Array<Object>} plans - Plans to scan.
 * @returns {Array<string>} The uuids of each plan's networkAffiliation, when present.
 */
export const extractNetworkAffiliationUuids = (plans) =>
  plans
    .filter((plan) => plan.networkAffiliation && plan.networkAffiliation.uuid)
    .map((plan) => plan.networkAffiliation.uuid)

/**
 * Decides whether the "save" action should be enabled for a given planAffiliations value.
 * @param {Object|null} planAffiliations - The candidate planAffiliations value.
 * @returns {boolean} True when planAffiliations is present and save should be enabled.
 */
export const resolveSaveEnabledState = (planAffiliations) => !!planAffiliations

/**
 * Builds the request body for the updatePlanNetworkAffiliationForClient API call.
 * @param {Object} planAffiliations - The draft's planAffiliations value (a selectedClient-shaped object).
 * @param {string} clientName - The client's display name.
 * @returns {{clientId: string, clientName: string, planType: string, affiliations: Array<Object>|null, shouldGetHrpPlans: boolean}}
 *   The request body to send to updatePlanNetworkAffiliationForClient.
 */
export const buildAffiliationRequestBody = (planAffiliations, clientName) => {
  const changeAffiliationRequestBody = {
    clientId: planAffiliations?.title,
    clientName,
    planType: planAffiliations?.planType,
    affiliations: [],
    shouldGetHrpPlans: true
  }

  if (planAffiliations?.title !== 'NONE' && planAffiliations?.plans) {
    for (const plan of planAffiliations?.plans?.filter((p) => p.networkAffiliation)) {
      changeAffiliationRequestBody.affiliations.push({
        planId: plan.planId,
        uuid: plan.networkAffiliation.uuid,
        networkName: plan.networkAffiliation.networkName
      })
    }
  } else {
    changeAffiliationRequestBody.affiliations = null
  }

  return changeAffiliationRequestBody
}

/**
 * Splits a list of plans into Javelina and HRP buckets based on the HRP_ planId prefix.
 * @param {Array<Object>} plans - Plans to partition.
 * @returns {{jvPlans: Array<Object>, hrpPlans: Array<Object>}} The partitioned plans.
 */
export const partitionPlansByNamespace = (plans) => ({
  jvPlans: plans.filter((p) => !p.planId.includes('HRP_')),
  hrpPlans: plans.filter((p) => p.planId.includes('HRP_'))
})

/**
 * Merges freshly saved network affiliations into the existing store plans.
 * Each plan is matched by planId; networkAffiliation may be null when an affiliation was removed.
 *
 * @param {Array<Object>} existingPlans - The full existing plans list from the store (jvPlans or hrpPlans).
 * @param {Array<Object>} updatedPlans - The subset of plans whose affiliation changed, from the save response.
 * @returns {Array<Object>} existingPlans with matching entries' networkAffiliation replaced.
 */
export const applyNetworkAffiliationUpdates = (existingPlans, updatedPlans) => {
  const planIdsToUpdate = updatedPlans.map((p) => p.planId)
  return existingPlans.map((p) => {
    if (planIdsToUpdate.includes(p.planId)) {
      const updatedPlan = updatedPlans.find((up) => up.planId === p.planId)
      p.networkAffiliation = updatedPlan?.networkAffiliation
    }
    return p
  })
}

// Plan affiliation UI state

const PLAN_AFFILIATION_NONE_SELECTED_CLIENT = { title: 'NONE', value: '' }

/**
 * Groups JV and HRP plans by clientId for plan-affiliation dropdown caches.
 * @param {Array<Object>} jvPlans - Javelina plans from the store.
 * @param {Array<Object>} hrpPlans - HRP plans from the store.
 * @returns {{ jvPlansGroupedByClientId: Object, hrpPlansGroupedByClientId: Object }}
 */
export const groupPlansForAffiliation = (jvPlans, hrpPlans) => ({
  jvPlansGroupedByClientId: groupPlansByClientId(jvPlans),
  hrpPlansGroupedByClientId: groupPlansByClientId(hrpPlans)
})

/**
 * Derives the saved configured-client snapshot from store plans and available networks.
 * @param {Object} params
 * @param {Array<Object>} params.jvPlans - Javelina plans from the store.
 * @param {Array<Object>} params.hrpPlans - HRP plans from the store.
 * @param {Array<{title: string, value: string}>} params.networkNames - Available network options.
 * @returns {Object|null} configuredClient snapshot or null when no affiliations match.
 */
export const resolveConfiguredClientFromStore = ({ jvPlans, hrpPlans, networkNames }) => {
  const affiliatedPlans = getPlansWithNetworkAffiliation(jvPlans, hrpPlans, networkNames)
  const derivedSelection = resolveClientAndPlanTypeFromPlans(affiliatedPlans)

  if (!derivedSelection) {
    return null
  }

  const clientPlans = affiliatedPlans.filter(
    (plan) => plan.clientId === derivedSelection.clientId
  )

  return buildConfiguredClientSnapshot(
    derivedSelection.clientId,
    derivedSelection.planType,
    clientPlans
  )
}

/**
 * Builds selectedClient for display with per-plan networkAffiliationOptions.
 * @param {Object} params
 * @param {string} params.clientId - Selected client ID.
 * @param {Array<Object>} params.plans - Plans belonging to the client.
 * @param {Array<{title: string, value: string}>} params.networkNames - Available network options.
 * @returns {{ title: string, value: string, plans: Array<Object> }}
 */
export const prepareSelectedClientForDisplay = ({ clientId, plans, networkNames }) => ({
  title: clientId,
  value: clientId,
  plans: buildPlansWithNetworkAffiliationOptions(plans, networkNames)
})

/**
 * Builds the client ID dropdown options for the plan affiliation tab.
 * @param {Object} params
 * @param {Object<string, Array<Object>>} params.plansGroupedByClientId - Plans grouped by clientId.
 * @param {Array<{title: string, value: string}>} params.networkNames - Available network options.
 * @param {string} params.currentClientName - Route client name being edited.
 * @param {string} params.selectedClientId - Currently selected client ID.
 * @returns {Array<Object>} Client dropdown options including NONE.
 */
export const prepareClientOptionsForDisplay = ({
  plansGroupedByClientId,
  networkNames,
  currentClientName,
  selectedClientId
}) => buildClientOptionsList(plansGroupedByClientId, networkNames, currentClientName, selectedClientId)

/**
 * Applies a user network-affiliation dropdown selection to selectedClient.plans.
 * @param {Object} params
 * @param {Object} params.selectedClient - Current selectedClient.
 * @param {string} params.planId - Plan whose affiliation changed.
 * @param {string} params.networkUuid - Selected network uuid.
 * @param {string} params.currentClientName - Route client name being edited.
 * @returns {Object} Updated selectedClient.
 */
export const applyUserNetworkAffiliationChange = ({
  selectedClient,
  planId,
  networkUuid,
  currentClientName
}) => ({
  ...selectedClient,
  plans: applyNetworkAffiliationSelection(
    selectedClient.plans,
    planId,
    networkUuid,
    currentClientName
  )
})

const getPlansGroupedByClientIdForPlanType = (planType, jvPlansGroupedByClientId, hrpPlansGroupedByClientId) =>
  planType === 'HRP' ? hrpPlansGroupedByClientId : jvPlansGroupedByClientId

const buildSelectedClientForPlanAffiliationState = ({
  clientId,
  planType,
  configuredClient,
  plansGroupedByClientId,
  networkNames
}) => {
  if (!clientId || clientId === 'NONE') {
    return PLAN_AFFILIATION_NONE_SELECTED_CLIENT
  }

  if (
    configuredClient &&
    clientId === configuredClient.title &&
    planType === configuredClient.planType
  ) {
    return prepareSelectedClientForDisplay({
      clientId,
      plans: JSON.parse(JSON.stringify(configuredClient.plans)),
      networkNames
    })
  }

  return buildSelectedClientFromGroupedPlans(clientId, plansGroupedByClientId, networkNames)
}

/**
 * Returns an empty plan-affiliation UI state.
 * @returns {Object} Empty planAffiliation state plus empty grouped caches.
 */
export const buildEmptyPlanAffiliationState = () => ({
  planType: '',
  clientId: '',
  configuredClient: null,
  selectedClient: PLAN_AFFILIATION_NONE_SELECTED_CLIENT,
  clientOptions: [],
  jvPlansGroupedByClientId: {},
  hrpPlansGroupedByClientId: {}
})

/**
 * Builds the full planAffiliation UI state from store plans and network options.
 * @param {Object} params
 * @param {Array<Object>} [params.jvPlans] - Javelina plans from the store.
 * @param {Array<Object>} [params.hrpPlans] - HRP plans from the store.
 * @param {Array<{title: string, value: string}>} [params.networkNames] - Available network options.
 * @param {string} params.currentClientName - Route client name being edited.
 * @param {{ planType: string, clientId: string }|null} [params.selection] - When set, preserves user selection.
 * @param {boolean} [params.reset] - When true, returns empty state.
 * @returns {Object} Full planAffiliation state plus grouped plan caches.
 */
export const buildPlanAffiliationState = ({
  jvPlans = [],
  hrpPlans = [],
  networkNames = [],
  currentClientName,
  selection = null,
  reset = false
}) => {
  if (reset) {
    return buildEmptyPlanAffiliationState()
  }

  const { jvPlansGroupedByClientId, hrpPlansGroupedByClientId } = groupPlansForAffiliation(
    jvPlans,
    hrpPlans
  )
  const configuredClient = resolveConfiguredClientFromStore({ jvPlans, hrpPlans, networkNames })

  let planType = ''
  let clientId = ''

  if (selection) {
    planType = selection.planType
    clientId = selection.clientId
  } else if (configuredClient) {
    planType = configuredClient.planType
    clientId = configuredClient.title
  }

  const plansGroupedByClientId = getPlansGroupedByClientIdForPlanType(
    planType,
    jvPlansGroupedByClientId,
    hrpPlansGroupedByClientId
  )

  const selectedClient = buildSelectedClientForPlanAffiliationState({
    clientId,
    planType,
    configuredClient,
    plansGroupedByClientId,
    networkNames
  })

  const clientOptions = prepareClientOptionsForDisplay({
    plansGroupedByClientId,
    networkNames,
    currentClientName,
    selectedClientId: clientId
  })

  return {
    planType,
    clientId,
    configuredClient,
    selectedClient,
    clientOptions,
    jvPlansGroupedByClientId,
    hrpPlansGroupedByClientId
  }
}
